'use client';

import { useEffect, useState } from 'react';
import Dropzone from 'react-dropzone';
import ReactCrop, { Crop } from 'react-image-crop';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { Button, ButtonSize, ButtonType } from '@nx-frontend/shared/ui/button';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { ImageTabs, ImageType, UploadSettings } from '@nx-frontend/shared/types';
import { IconKey } from '@nx-frontend/shared/types/icon';
import { parseImage } from '@nx-frontend/shared/utils';
import { EmojiSelector } from './emoji-selector';
import { EmojiView } from './emoji-view';
import { IconSelector } from './icon-selector';
import {
  $imageSelector,
  $imageSelectorActiveTab,
  $imageSelectorTabs,
  resetImageSelectorEvent,
  setActiveTabEvent,
  setUploadSettingsEvent,
  updateImageSelectorEvent,
  updateTabsEvent,
} from './store/image-selector';

type ImageTabProps = Partial<ImageTabs> & Required<Pick<ImageTabs, 'type'>>;

interface ImageSelectorProps {
  image?: string;
  imageTabs?: ImageTabProps[];
  onCantGetImage: (value: boolean) => void;
  onFileSelect?: () => void;
  uploadSettings?: UploadSettings;
  dragDropDescription?: string[];
}

export function ImageSelector({
  image,
  imageTabs,
  uploadSettings,
  onCantGetImage,
  onFileSelect,
  dragDropDescription,
}: ImageSelectorProps) {
  const [
    imageSelector,
    imageSelectorTabs,
    imageSelectorActiveTab,
    updateImageSelector,
    updateTabs,
    setActiveTab,
    resetImageSelector,
    setUploadSettings,
  ] = useUnit([
    $imageSelector,
    $imageSelectorTabs,
    $imageSelectorActiveTab,
    updateImageSelectorEvent,
    updateTabsEvent,
    setActiveTabEvent,
    resetImageSelectorEvent,
    setUploadSettingsEvent,
  ]);
  const t = useTranslations('shared');
  const [imageType, setImageType] = useState(ImageType.URL);
  const [imageValue, setImageValue] = useState('');
  // const [externalUrl, setExternalUrl] = useState('');
  // const [externalUrlError, setExternalUrlError] = useState(false);
  const [crop, setCrop] = useState<Crop>();

  useEffect(() => {
    if (imageTabs) {
      updateTabs(imageTabs as ImageTabs[]);
    }

    if (uploadSettings) {
      setUploadSettings(uploadSettings);
    }

    return () => {
      resetImageSelector();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (imageSelectorActiveTab === ImageType.EMOJI) {
      onCantGetImage(!imageSelector.previewEmoji);
    } else if (imageSelectorActiveTab === ImageType.ICON) {
      onCantGetImage(!imageSelector.previewIcon);
    } else if (imageSelectorActiveTab === ImageType.URL) {
      onCantGetImage(
        // externalUrlError ||
        imageSelector.previewImageError ||
          imageSelector.previewImage === '' ||
          (imageSelector.previewImage === imageValue &&
            (imageSelector.completedCrop === undefined ||
              imageSelector.completedCrop?.height === 0 ||
              imageSelector.completedCrop?.width === 0))
      );
    }
  }, [
    imageValue,
    // externalUrlError,
    imageSelectorActiveTab,
    imageSelector.previewEmoji,
    imageSelector.previewIcon,
    imageSelector.previewImageError,
    imageSelector.previewImage,
    imageSelector.completedCrop,
    onCantGetImage,
  ]);

  useEffect(() => {
    if (image) {
      const { type, value } = parseImage(image);
      setImageType(type);
      setImageValue(value);
    }
  }, [image]);

  useEffect(() => {
    if (imageType && imageValue) {
      setActiveTab(imageType);
      if (imageType === ImageType.EMOJI) {
        updateImageSelector({ previewEmoji: imageValue });
      } else if (imageType === ImageType.ICON) {
        updateImageSelector({ previewIcon: imageValue });
      } else if (imageType === ImageType.URL) {
        updateImageSelector({ previewImage: imageValue });
      }
    }
  }, [imageType, imageValue, setActiveTab, updateImageSelector]);

  useEffect(() => {
    updateImageSelector({
      cropImage: imageSelector.previewImage
        ? imageSelector.previewImage
        : imageType === ImageType.URL
        ? imageValue
        : '',
    });
  }, [imageSelector.previewImage, imageType, imageValue, updateImageSelector]);

  const tabButton = (active: boolean, name: ImageType, title: string) => {
    return (
      <Button
        type={ButtonType.STROKED}
        size={ButtonSize.SM}
        className={classNames('!px-2', {
          'border-transparent hover:border-transparent': !active,
        })}
        onClick={
          !active
            ? () => {
                setActiveTab(name);
              }
            : undefined
        }
      >
        <span>{title}</span>
      </Button>
    );
  };

  return (
    <div className="image-selector">
      {imageSelectorTabs.filter((tab) => {
        return tab.active;
      }).length > 1 ? (
        <div className="flex justify-between items-center px-4">
          {imageSelectorTabs.map((tab) => {
            return tab.active ? (
              <div key={tab.type}>{tabButton(imageSelectorActiveTab === tab.type, tab.type, tab.name)}</div>
            ) : null;
          })}
        </div>
      ) : null}
      {imageSelectorTabs.filter((tab) => {
        return tab.type === ImageType.EMOJI && tab.active;
      }).length && imageSelectorActiveTab === ImageType.EMOJI ? (
        <div>
          {imageSelector.previewEmoji ? (
            <div className="w-12 h-12 mx-4 my-2">
              <EmojiView unified={imageSelector.previewEmoji} />
            </div>
          ) : null}
          <EmojiSelector
            onEmojiSelect={(emojiData) => {
              updateImageSelector({ previewEmoji: emojiData.unified });
            }}
          />
        </div>
      ) : null}
      {imageSelectorTabs.filter((tab) => {
        return tab.type === ImageType.ICON && tab.active;
      }).length && imageSelectorActiveTab === ImageType.ICON ? (
        <div className="text-neutral-600">
          {imageSelector.previewIcon ? (
            <Icon icon={IconsMap[imageSelector.previewIcon as IconKey]} className="w-12 h-12 mx-4 my-2" />
          ) : null}
          <IconSelector
            onIconSelect={(iconKey) => {
              updateImageSelector({ previewIcon: iconKey });
            }}
          />
        </div>
      ) : null}
      {imageSelectorTabs.filter((tab) => {
        return tab.type === ImageType.URL && tab.active;
      }).length && imageSelectorActiveTab === ImageType.URL ? (
        <>
          {/* TODO temp disabled input for image url */}
          {/* <div className="p-4">
            <UiInput
              id="image_url"
              type="text"
              label="Paste link to an image"
              name="image_url"
              value={externalUrl}
              error={externalUrlError}
              placeholder="https://"
              view={UiInputView.LABEL_TOP}
              onChange={(e) => {
                const error = e.target.value !== '' ? !isValidUrl(e.target.value) : false;
                updateImageSelector({ file: undefined });
                setExternalUrlError(error);
                if (!error) {
                  updateImageSelector({ previewImageError: false });
                }
                setExternalUrl(e.target.value);
                updateImageSelector({ previewImage: !error ? e.target.value : '' });
              }}
            />
          </div> */}
          <div className="p-4 text-neutral-500">
            <Dropzone
              onDrop={(files) => {
                // setExternalUrl('');
                updateImageSelector({ file: files[0] });
                onFileSelect?.();
              }}
              multiple={false}
              accept={{
                'image/*': [],
              }}
            >
              {({ getRootProps, getInputProps }) => (
                <div
                  {...getRootProps({
                    className:
                      'w-full h-[64px] border-2 border-neutral-500 border-dashed flex flex-col items-center justify-center cursor-pointer rounded-md',
                  })}
                >
                  <input {...getInputProps()} />
                  <Icon icon={IconsMap.UPLOAD} className="w-4 h-4" />
                  <span>{t('image-selector.upload_file')}</span>
                </div>
              )}
            </Dropzone>
            {dragDropDescription ? (
              <div className="flex flex-col text-xs mt-2 gap-1">
                {dragDropDescription.map((item, key) => {
                  return <span key={key}>{item}</span>;
                })}
              </div>
            ) : null}
          </div>
          {imageSelector.previewImage || (imageType === ImageType.URL && imageValue) ? (
            <div className="w-full px-4 pt-4">
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => updateImageSelector({ completedCrop: c })}
                className="w-full"
              >
                <img
                  alt=""
                  className="w-full"
                  onLoad={(e) => {
                    updateImageSelector({
                      previewImageError: false,
                      cropSize: {
                        width: e.currentTarget.offsetWidth,
                        height: e.currentTarget.offsetHeight,
                      },
                    });
                  }}
                  onError={() => {
                    updateImageSelector({ previewImageError: true });
                  }}
                  src={imageSelector.cropImage}
                />
              </ReactCrop>
              {imageSelector.previewImageError ? (
                <div className="text-sm text-red-600 text-center">This image cannot be loaded</div>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
